/**
 * LabImportDialog
 *
 * Three-step dialog for importing lab results from a file (PDF, image, text).
 *
 * Step 1 — Drop:    drag-and-drop / browse area
 * Step 2 — Parse:   uploading + AI extraction spinner
 * Step 3 — Review:  editable table of parsed results; user confirms or edits
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
  TextField, Chip, Tooltip, Alert, Divider,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CheckCircle as OkIcon,
  Warning as WarnIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as AIIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../utils/api';
import { todayLocalISO } from '../utils/dateUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ResultRow {
  testName: string;
  value: string;
  unit: string;
  referenceRange: string;
  isAbnormal: boolean;
  notes: string;
}

interface ParsedData {
  labProvider: string;
  accessionNumber: string;
  reportDate: string;
  panelName: string;
  performedBy: string;
  results: ResultRow[];
}

type Step = 'drop' | 'parsing' | 'review';

interface Props {
  open: boolean;
  onClose: () => void;
  horseId: string;
  onImported: () => void;   // called after successful save to refresh the list
}

const ACCEPTED_TYPES = 'application/pdf,image/jpeg,image/png,image/webp,text/plain,.hl7';

/** The server's validation answer, with `results.3.unit` read as the table row the vet sees.
 *  `tableRows[i]` is the table position of the i-th row sent (blank rows are not sent). */
function saveRefusal(message: unknown, tableRows: number[]): string {
  const lines = (Array.isArray(message) ? message : [message]).filter(Boolean).map(String);
  if (!lines.length) return 'Failed to save lab result';
  return lines
    .map(l => l.replace(/^results\.(\d+)\.(\w+)/, (_, i, field) => `Row ${(tableRows[Number(i)] ?? Number(i)) + 1} ${field}`))
    .join('. ');
}

// ─── Component ────────────────────────────────────────────────────────────────

const LabImportDialog: React.FC<Props> = ({ open, onClose, horseId, onImported }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [step, setStep] = useState<Step>('drop');
  const [dragging, setDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedData>({
    labProvider: '', accessionNumber: '', reportDate: '', panelName: '', performedBy: '', results: [],
  });
  const [saving, setSaving] = useState(false);
  const [parseWarning, setParseWarning] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The last body sent and the id it went under: pressing Confirm again on an unchanged review
  // reuses the id, so a save whose answer was lost lands once (the server replays it).
  const lastAttempt = useRef<{ body: string; id: string } | null>(null);

  // ─── Reset on close ────────────────────────────────────────────────────────
  const handleClose = () => {
    if (saving) return;
    setStep('drop');
    setFile(null);
    setParsed({ labProvider: '', accessionNumber: '', reportDate: '', panelName: '', performedBy: '', results: [] });
    setParseWarning('');
    lastAttempt.current = null;
    onClose();
  };

  // ─── File selection ────────────────────────────────────────────────────────
  const handleFile = useCallback(async (f: File) => {
    setFile(f);
    setStep('parsing');
    setParseWarning('');

    const formData = new FormData();
    formData.append('file', f);

    try {
      const { data } = await api.post('/lab-results/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // The server gives the AI read 90 s; the app-wide 30 s gave up on real reports that took
        // 31-41 s, while the server finished the read anyway.
        timeout: 120_000,
      });

      const p = data.parsed as ParsedData;
      // Every box holds a string: the save validates strings, and a number cannot be trimmed.
      const text = (v: unknown) => (v == null ? '' : String(v));
      const rows: ResultRow[] = (p.results || []).map(r => ({
        testName: text(r.testName),
        value: text(r.value),
        unit: text(r.unit),
        referenceRange: text(r.referenceRange),
        isAbnormal: !!r.isAbnormal,
        notes: text(r.notes),
      }));

      setParsed({
        labProvider: text(p.labProvider),
        accessionNumber: text(p.accessionNumber),
        reportDate: text(p.reportDate),
        panelName: text(p.panelName),
        performedBy: text(p.performedBy),
        results: rows,
      });

      if (rows.length === 0) {
        setParseWarning('No individual test values were found. You can add them manually below.');
      }

      setStep('review');
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to parse file', { variant: 'error' });
      setStep('drop');
    }
  }, [enqueueSnackbar]);

  // ─── Drag-and-drop handlers ────────────────────────────────────────────────
  const onDragOver = (e: React.DragEvent) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  // ─── Row editing ───────────────────────────────────────────────────────────
  const updateRow = (i: number, field: keyof ResultRow, value: string | boolean) => {
    setParsed(prev => {
      const rows = [...prev.results];
      rows[i] = { ...rows[i], [field]: value };
      return { ...prev, results: rows };
    });
  };

  const addRow = () => {
    setParsed(prev => ({
      ...prev,
      results: [...prev.results, { testName: '', value: '', unit: '', referenceRange: '', isAbnormal: false, notes: '' }],
    }));
  };

  const deleteRow = (i: number) => {
    setParsed(prev => ({ ...prev, results: prev.results.filter((_, idx) => idx !== i) }));
  };

  // ─── Confirm / Save ────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!parsed.labProvider.trim()) {
      enqueueSnackbar('Lab provider is required', { variant: 'warning' });
      return;
    }
    // Drop incomplete rows before posting — addRow() seeds a BLANK row, and an OCR/AI parse can
    // return one with no test name. POST /lab-results validates every element since the F2 seam,
    // so one blank row 400s the ENTIRE import. Same filter the sibling writer already applies
    // (LabsDiagnosticsPanel handleSave). Empty after filtering is a warning, not a silent no-op.
    const sentRows = parsed.results.flatMap((r, i) => (r.testName.trim() && r.value.trim() ? [i] : []));
    const validRows = sentRows.map((i) => parsed.results[i]);
    if (!validRows.length) {
      enqueueSnackbar('Add at least one test with a name and a value', { variant: 'warning' });
      return;
    }
    const body = {
      horseId,
      labProvider: parsed.labProvider,
      accessionNumber: parsed.accessionNumber || undefined,
      reportDate: parsed.reportDate || todayLocalISO(),
      panelName: parsed.panelName || undefined,
      performedBy: parsed.performedBy || undefined,
      results: validRows,
    };
    const key = JSON.stringify(body);
    if (lastAttempt.current?.body !== key) lastAttempt.current = { body: key, id: crypto.randomUUID() };
    setSaving(true);
    try {
      await api.post('/lab-results', { ...body, id: lastAttempt.current.id });
      enqueueSnackbar('Lab result imported successfully', { variant: 'success' });
      onImported();
      handleClose();
    } catch (err: any) {
      enqueueSnackbar(
        err?.response
          ? saveRefusal(err.response.data?.message, sentRows)
          : "The import didn't finish. Your reviewed results are still here: press Confirm Import to try again, and it will only be saved once.",
        { variant: 'error' },
      );
    } finally {
      setSaving(false);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  const abnormalCount = parsed.results.filter(r => r.isAbnormal).length;

  return (
    <Dialog
      open={open}
      // A stray click outside or Escape must not throw away a review; Cancel still closes.
      onClose={() => { if (step !== 'review') handleClose(); }}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { height: step === 'review' ? '90vh' : 'auto' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AIIcon color="primary" />
            <Typography variant="h6">Import Lab Results</Typography>
            {step === 'review' && (
              <Chip
                label={`${parsed.results.length} tests${abnormalCount > 0 ? ` · ${abnormalCount} flagged` : ''}`}
                size="small"
                color={abnormalCount > 0 ? 'error' : 'primary'}
                variant="outlined"
              />
            )}
          </Box>
          <IconButton onClick={handleClose} size="small" disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Step indicator */}
        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
          {(['drop', 'parsing', 'review'] as Step[]).map((s, i) => (
            <React.Fragment key={s}>
              <Chip
                label={s === 'drop' ? '1 Upload' : s === 'parsing' ? '2 Parsing' : '3 Review'}
                size="small"
                color={step === s ? 'primary' : 'default'}
                variant={step === s ? 'filled' : 'outlined'}
              />
              {i < 2 && <Typography variant="caption" color="text.disabled">›</Typography>}
            </React.Fragment>
          ))}
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ p: step === 'review' ? 0 : 3, overflow: 'auto' }}>

        {/* ── Step 1: Drop ────────────────────────────────────────────────── */}
        {step === 'drop' && (
          <Box
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragging ? 'primary.main' : 'divider',
              borderRadius: 2,
              bgcolor: dragging ? 'primary.50' : 'background.default',
              minHeight: 240,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              cursor: 'pointer',
              transition: 'all 0.15s',
              '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
            }}
          >
            <UploadIcon sx={{ fontSize: 56, color: dragging ? 'primary.main' : 'text.disabled' }} />
            <Box textAlign="center">
              <Typography variant="h6" color={dragging ? 'primary' : 'text.primary'}>
                Drop your lab report here
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                or click to browse
              </Typography>
              <Typography variant="caption" color="text.disabled" mt={1} display="block">
                Supports PDF, PNG, JPG — Antech, IDEXX, Zoetis, and most reference labs
              </Typography>
            </Box>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
          </Box>
        )}

        {/* ── Step 2: Parsing ─────────────────────────────────────────────── */}
        {step === 'parsing' && (
          <Box display="flex" flexDirection="column" alignItems="center" gap={3} py={6}>
            <CircularProgress size={56} />
            <Box textAlign="center">
              <Typography variant="h6">Parsing {file?.name}</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                AI is extracting test values, reference ranges, and flags…
              </Typography>
            </Box>
            <LinearProgress sx={{ width: '60%', borderRadius: 1 }} />
          </Box>
        )}

        {/* ── Step 3: Review ──────────────────────────────────────────────── */}
        {step === 'review' && (
          <Box display="flex" flexDirection="column" height="100%">

            {parseWarning && (
              <Alert severity="warning" sx={{ mx: 3, mt: 2 }} icon={<WarnIcon />}>
                {parseWarning}
              </Alert>
            )}

            {/* ── Header fields ── */}
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} p={3} pb={2}>
              <TextField
                label="Lab Provider *"
                size="small"
                value={parsed.labProvider}
                onChange={e => setParsed(p => ({ ...p, labProvider: e.target.value }))}
                helperText="e.g. Antech, IDEXX, Zoetis, in-house"
              />
              <TextField
                label="Panel / Test Name"
                size="small"
                value={parsed.panelName}
                onChange={e => setParsed(p => ({ ...p, panelName: e.target.value }))}
              />
              <TextField
                label="Report Date"
                size="small"
                type="date"
                value={parsed.reportDate}
                onChange={e => setParsed(p => ({ ...p, reportDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Accession / Order #"
                size="small"
                value={parsed.accessionNumber}
                onChange={e => setParsed(p => ({ ...p, accessionNumber: e.target.value }))}
              />
              <TextField
                label="Performed By"
                size="small"
                value={parsed.performedBy}
                onChange={e => setParsed(p => ({ ...p, performedBy: e.target.value }))}
              />
              <Box display="flex" alignItems="center" gap={1}>
                <OkIcon color="success" fontSize="small" />
                <Typography variant="caption" color="text.secondary">
                  AI-extracted · review before saving
                </Typography>
              </Box>
            </Box>

            <Divider />

            {/* ── Results table ── */}
            <Box flex={1} overflow="auto">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 180, fontWeight: 600 }}>Test Name</TableCell>
                    <TableCell sx={{ minWidth: 100, fontWeight: 600 }}>Value</TableCell>
                    <TableCell sx={{ minWidth: 80, fontWeight: 600 }}>Unit</TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Ref Range</TableCell>
                    <TableCell align="center" sx={{ minWidth: 80, fontWeight: 600 }}>Flag</TableCell>
                    <TableCell sx={{ minWidth: 120, fontWeight: 600 }}>Notes</TableCell>
                    <TableCell sx={{ width: 40 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsed.results.map((row, i) => (
                    <TableRow
                      key={i}
                      sx={row.isAbnormal ? { bgcolor: 'error.50' } : undefined}
                    >
                      <TableCell>
                        <TextField
                          variant="standard"
                          size="small"
                          value={row.testName}
                          onChange={e => updateRow(i, 'testName', e.target.value)}
                          fullWidth
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="standard"
                          size="small"
                          value={row.value}
                          onChange={e => updateRow(i, 'value', e.target.value)}
                          inputProps={{ style: { fontSize: 13, fontWeight: row.isAbnormal ? 700 : 400 } }}
                          sx={{ width: 90 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="standard"
                          size="small"
                          value={row.unit}
                          onChange={e => updateRow(i, 'unit', e.target.value)}
                          sx={{ width: 70 }}
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="standard"
                          size="small"
                          value={row.referenceRange}
                          onChange={e => updateRow(i, 'referenceRange', e.target.value)}
                          sx={{ width: 110 }}
                          inputProps={{ style: { fontSize: 13 } }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={row.isAbnormal ? 'Click to mark normal' : 'Click to mark abnormal'}>
                          <Chip
                            label={row.isAbnormal ? 'H/L' : 'WNL'}
                            size="small"
                            color={row.isAbnormal ? 'error' : 'success'}
                            variant={row.isAbnormal ? 'filled' : 'outlined'}
                            onClick={() => updateRow(i, 'isAbnormal', !row.isAbnormal)}
                            sx={{ cursor: 'pointer', minWidth: 46 }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <TextField
                          variant="standard"
                          size="small"
                          value={row.notes}
                          onChange={e => updateRow(i, 'notes', e.target.value)}
                          placeholder="optional"
                          inputProps={{ style: { fontSize: 13 } }}
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => deleteRow(i)}>
                          <DeleteIcon fontSize="inherit" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Add row button */}
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={addRow}
                        sx={{ ml: -0.5 }}
                      >
                        Add test
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {step === 'review' && (
          <Button
            size="small"
            onClick={() => { setStep('drop'); setFile(null); lastAttempt.current = null; }}
            disabled={saving}
          >
            ← Upload Different File
          </Button>
        )}
        <Box flex={1} />
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        {step === 'review' && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={saving || !parsed.labProvider.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : <OkIcon />}
          >
            {saving ? 'Saving…' : 'Confirm Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default LabImportDialog;
